import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../context/ThemeContext";
import { getLang } from "../lang/useLanguage";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });

const um = () => getLang().userManagement || {};
const roleLabel = (roleKey) =>
  um().roles?.[ROLES[roleKey]?.labelKey || roleKey] || roleKey;
const moduleLabel = (mod) =>
  um().modules?.[mod?.labelKey || mod?.id] || mod?.id || "—";


const ROLES = {
  admin:                    { labelKey: "admin",        color: "#f87171", icon: "👑" },
  manager:                  { labelKey: "manager",               color: "#a78bfa", icon: "🏢" },
  production_manager:       { labelKey: "production_manager",             color: "#3b82f6", icon: "🏭" },
  quality_manager:          { labelKey: "quality_manager",             color: "#10b981", icon: "✅" },
  warehouse_chief:          { labelKey: "warehouse_chief",                 color: "#f59e0b", icon: "📦" },
  maintenance_chief:        { labelKey: "maintenance_chief",                color: "#f97316", icon: "🔧" },
  hr_manager:               { labelKey: "hr_manager",                 color: "#ec4899", icon: "👥" },
  accountant:               { labelKey: "accountant",                  color: "#14b8a6", icon: "💰" },
  technician:               { labelKey: "technician",                 color: "#60a5fa", icon: "⚙️" },
  warehouse:                { labelKey: "warehouse",            color: "#fbbf24", icon: "🏪" },
  operator:                 { labelKey: "operator",                  color: "#94a3b8", icon: "👷" },
  purchasing:               { labelKey: "purchasing",                color: "#06b6d4", icon: "🛒" },
  // ── DIŞ TİCARET
  dis_ticaret_mudur:      { labelKey: "dis_ticaret_mudur",     color: "#8b5cf6", icon: "✈️", dept: "Dış Ticaret" },
  dis_ticaret_uzman:      { labelKey: "dis_ticaret_uzman",     color: "#a78bfa", icon: "✈️", dept: "Dış Ticaret" },
  dis_ticaret_temsilci:   { labelKey: "dis_ticaret_temsilci", color: "#c4b5fd", icon: "✈️", dept: "Dış Ticaret" },
  // ── YURT İÇİ SATIŞ
  ic_satis_mudur:         { labelKey: "ic_satis_mudur",     color: "#10b981", icon: "🏠", dept: "Yurt İçi Satış" },
  ic_satis_uzman:         { labelKey: "ic_satis_uzman",     color: "#34d399", icon: "🏠", dept: "Yurt İçi Satış" },
  ic_satis_temsilci:      { labelKey: "ic_satis_temsilci", color: "#6ee7b7", icon: "🏠", dept: "Yurt İçi Satış" },
};

const SALES_ROLES = [
  "dis_ticaret_mudur","dis_ticaret_uzman","dis_ticaret_temsilci",
  "ic_satis_mudur","ic_satis_uzman","ic_satis_temsilci",
];

const MODULES = [
  { id: "dashboard",   labelKey: "dashboard",           icon: "📊" },
  { id: "machines",    labelKey: "machines",            icon: "🏭" },
  { id: "workorders",  labelKey: "workorders",          icon: "📋" },
  { id: "quality",     labelKey: "quality",    icon: "✅" },
  { id: "stock",       labelKey: "stock",                 icon: "📦" },
  { id: "maintenance", labelKey: "maintenance",       icon: "🔧" },
  { id: "energy",      labelKey: "energy",               icon: "⚡" },
  { id: "kaizen",      labelKey: "kaizen",               icon: "🔄" },
  { id: "lean",        labelKey: "lean",                 icon: "🎯" },
  { id: "fives",       labelKey: "fives",           icon: "5️⃣" },
  { id: "hr",          labelKey: "hr",          icon: "👥" },
  { id: "shifts",      labelKey: "shifts",     icon: "📅" },
  { id: "reports",     labelKey: "reports",             icon: "📈" },
  { id: "admin",       labelKey: "admin",       icon: "🎛️" },
  { id: "users",       labelKey: "users",   icon: "🔐" },
  { id: "financial",   labelKey: "financial",             icon: "💹" },
  { id: "ncr",         labelKey: "ncr",           icon: "🔴" },
  { id: "scrap",       labelKey: "scrap", icon: "♻️" },
  { id: "products",    labelKey: "products",    icon: "📦" },
  { id: "salesorders",       labelKey: "sales",    icon: "🛒" },
  { id: "purchasing",  labelKey: "purchasing",           icon: "🛍️" },
];

const DEFAULT_PERMISSIONS = {
  admin:                    ["*"],
  manager:                  ["*"],
  production_manager:       ["dashboard","machines","workorders","quality","stock","energy","kaizen","reports"],
  quality_manager:          ["dashboard","machines","quality","kaizen","reports"],
  warehouse_chief:          ["dashboard","stock","workorders","reports"],
  maintenance_chief:        ["dashboard","machines","maintenance","energy","reports"],
  hr_manager:               ["dashboard","hr","shifts","reports"],
  accountant:               ["dashboard","reports","financial"],
  technician:               ["dashboard","machines","maintenance"],
  warehouse:                ["dashboard","stock"],
  operator:                 ["dashboard","machines"],
  dis_ticaret_mudur:      ["dashboard","salesorders","customers","reports"],
  dis_ticaret_uzman:      ["dashboard","salesorders","customers"],
  dis_ticaret_temsilci:   ["dashboard","salesorders","customers"],
  ic_satis_mudur:         ["dashboard","salesorders","customers","reports"],
  ic_satis_uzman:         ["dashboard","salesorders","customers"],
  ic_satis_temsilci:      ["dashboard","salesorders","customers"],
};

// ─── KULLANICI FORM MODAL ─────────────────────────────────────────────────
const UserFormModal = ({ data, onClose, onSaved, t, allUsers }) => {
  const isEdit = !!data;
  const [form, setForm] = useState({
    username:     data?.username     || "",
    password:     "",
    role:         data?.role         || "operator",
    sales_region: data?.sales_region || null,
    sales_level:  data?.sales_level  || null,
    reports_to:   data?.reports_to   || null,
    is_active:    data?.is_active !== undefined ? data.is_active : true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const isSalesRole = SALES_ROLES.includes(form.role);

  // Rol seçilince otomatik sales_region ve sales_level ata
  const handleRoleChange = (newRole) => {
    let region = null, level = null;
    if (newRole.startsWith("dis_ticaret_")) {
      region = "foreign";
      const rawLevel = newRole.replace("dis_ticaret_", "");
      level = {
        mudur: "manager",
        uzman: "chief",
        temsilci: "rep",
      }[rawLevel] || null;
    } else if (newRole.startsWith("ic_satis_")) {
      region = "domestic";
      level  = newRole.replace("ic_satis_", "");
    }
    setForm(p => ({ ...p, role: newRole, sales_region: region, sales_level: level }));
  };

  // Aynı bölgedeki müdür/şefleri filtrele (reports_to için)
  const potentialManagers = (allUsers || []).filter(u => {
    if (!form.role) return false;
    if (form.role.startsWith("dis_ticaret_")) {
      return ["dis_ticaret_mudur","dis_ticaret_uzman"].includes(u.role) && u.id !== data?.id;
    }
    if (form.role.startsWith("ic_satis_")) {
      return ["ic_satis_mudur","ic_satis_uzman"].includes(u.role) && u.id !== data?.id;
    }
    return false;
  });

  const handleSave = async () => {
    if (!form.username) { setError(um().usernameRequired); return; }
    if (!isEdit && !form.password) { setError(um().passwordRequired); return; }
    setLoading(true); setError("");
    try {
      const url    = isEdit ? `${API}/users/${data.id}` : `${API}/users`;
      const method = isEdit ? "PUT" : "POST";
      const body   = isEdit
        ? {
            role: form.role,
            is_active: form.is_active,
            sales_region: form.sales_region,
            sales_level: form.sales_level,
            reports_to: form.reports_to,
            ...(form.password ? { password: form.password } : {})
          }
        : form;
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json()).detail || um().genericError);
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px",
    background: t.bg.input, border: `1px solid ${t.border.input}`,
    borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box",
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay,
      backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`,
        borderRadius: 20, width: "100%", maxWidth: 480,
        overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`,
          display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>
            {isEdit ? `✏️ ${um().editUser}` : `➕ ${um().newUser}`}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none",
            color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Kullanıcı Adı */}
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
              textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{um().username} *</label>
            <input type="text" value={form.username} disabled={isEdit}
              onChange={e => set("username", e.target.value)}
              style={{ ...inputStyle, opacity: isEdit ? 0.6 : 1 }} />
          </div>

          {/* Şifre */}
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
              textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
              {isEdit ? um().newPassword : `${um().password} *`}
            </label>
            <input type="password" value={form.password}
              onChange={e => set("password", e.target.value)}
              placeholder={isEdit ? um().passwordLeaveBlank : ""}
              style={inputStyle} />
          </div>

          {/* Rol */}
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
              textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{um().role} *</label>
            <select value={form.role} onChange={e => handleRoleChange(e.target.value)} style={inputStyle}>
              {Object.entries(ROLES).map(([k, v]) => (
                <option key={k} value={k}>{v.icon} {roleLabel(k)}</option>
              ))}
            </select>
          </div>

          {/* Satış Hiyerarşisi — sadece satış rollerinde */}
          {isSalesRole && (
            <div style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#10b981",
                textTransform: "uppercase", letterSpacing: "0.06em" }}>
                📊 {um().salesHierarchy}
              </div>

              {/* Otomatik dolan bilgiler */}
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1, background: t.bg.input, border: `1px solid ${t.border.input}`,
                  borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
                  <div style={{ color: t.text.muted, marginBottom: 2, fontSize: 10 }}>{um().region}</div>
                  <div style={{ color: form.sales_region === "domestic" ? "#10b981" : "#8b5cf6", fontWeight: 700 }}>
                    {form.sales_region === "domestic"
                      ? `🏠 ${um().regions?.domestic}`
                      : form.sales_region === "foreign"
                      ? `✈️ ${um().regions?.foreign}`
                      : "—"}
                  </div>
                </div>
                <div style={{ flex: 1, background: t.bg.input, border: `1px solid ${t.border.input}`,
                  borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
                  <div style={{ color: t.text.muted, marginBottom: 2, fontSize: 10 }}>{um().level}</div>
                  <div style={{ color: t.text.primary, fontWeight: 700 }}>
                    {form.sales_level === "manager"
                      ? `🏆 ${um().levels?.manager}`
                      : form.sales_level === "chief"
                      ? `⭐ ${um().levels?.chief}`
                      : form.sales_level === "rep"
                      ? `👤 ${um().levels?.representative}`
                      : "—"}
                  </div>
                </div>
              </div>

              {/* {um().reportsTo} (manager hariç) */}
              {form.sales_level !== "mudur" && (
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label,
                    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                    {um().reportsTo}
                  </label>
                  <select value={form.reports_to || ""} onChange={e => set("reports_to", e.target.value || null)} style={inputStyle}>
                    <option value="">{um().select}</option>
                    {potentialManagers.map(u => (
                      <option key={u.id} value={u.username}>
                        {ROLES[u.role]?.icon} {u.username} ({roleLabel(u.role)})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Aktif */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input type="checkbox" id="is_active" checked={form.is_active}
              onChange={e => set("is_active", e.target.checked)}
              style={{ width: 16, height: 16, cursor: "pointer" }} />
            <label htmlFor="is_active" style={{ fontSize: 13, color: t.text.secondary, cursor: "pointer" }}>
              {um().activeUser}
            </label>
          </div>

          {error && (
            <div style={{ padding: "10px 14px", background: t.accent.redBg,
              border: `1px solid ${t.accent.red}44`, borderRadius: 8,
              color: t.accent.red, fontSize: 13 }}>{error}</div>
          )}
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`,
          display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8,
            border: `1px solid ${t.border.input}`, background: "none",
            color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{um().cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{
            padding: "10px 24px", borderRadius: 8, border: "none",
            background: loading ? t.bg.tag : "linear-gradient(135deg,#3b82f6,#6366f1)",
            color: "#fff", cursor: loading ? "default" : "pointer",
            fontSize: 13, fontWeight: 600,
          }}>{loading ? um().saving : isEdit ? um().update : um().create}</button>
        </div>
      </div>
    </div>
  );
};

// ─── ANA SAYFA ────────────────────────────────────────────────────────────
export default function UserManagement() {
  const { t, themeName } = useTheme();
  const [mainTab, setMainTab]           = useState(localStorage.getItem("tab_usermgmt") || "users");
  const [users, setUsers]               = useState([]);
  const [permissions, setPermissions]   = useState(DEFAULT_PERMISSIONS);
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editing, setEditing]           = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedRole, setSelectedRole] = useState("operator");
  const [permSaved, setPermSaved]       = useState(false);
  const currentUser = localStorage.getItem("username");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/users`, { headers: headers() }),
        fetch(`${API}/users/permissions`, { headers: headers() }),
      ]);
      if (r1.ok) setUsers(await r1.json());
      if (r2.ok) {
        const d = await r2.json();
        if (d.all_roles) setPermissions(d.all_roles);
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    try {
      await fetch(`${API}/users/${id}`, { method: "DELETE", headers: headers() });
      setDeleteConfirm(null);
      fetchData();
    } catch {}
  };

  const toggleActive = async (user) => {
    try {
      await fetch(`${API}/users/${user.id}`, {
        method: "PUT", headers: headers(),
        body: JSON.stringify({ is_active: !user.is_active }),
      });
      fetchData();
    } catch {}
  };

  const toggleModulePermission = (role, moduleId) => {
    setPermissions(prev => {
      const current = prev[role] || [];
      if (current.includes("*")) return prev;
      const updated = current.includes(moduleId)
        ? current.filter(m => m !== moduleId)
        : [...current, moduleId];
      return { ...prev, [role]: updated };
    });
  };

  const preserveHiddenPermissions = (current) => {
    const editorModuleIds = new Set(MODULES.map(m => m.id));

    return current.filter(
      permission =>
        permission !== "*" &&
        !editorModuleIds.has(permission)
    );
  };

  const selectAllModulePermissions = (role) => {
    setPermissions(prev => {
      const current = prev[role] || [];
      if (current.includes("*")) return prev;

      const hidden = preserveHiddenPermissions(current);
      const updated = [
        ...MODULES.map(m => m.id),
        ...hidden,
      ];

      return {
        ...prev,
        [role]: [...new Set(updated)],
      };
    });
  };

  const clearModulePermissions = (role) => {
    setPermissions(prev => {
      const current = prev[role] || [];
      if (current.includes("*")) return prev;

      const hidden = preserveHiddenPermissions(current);
      const updated = [
        "dashboard",
        ...hidden,
      ];

      return {
        ...prev,
        [role]: [...new Set(updated)],
      };
    });
  };

  const savePermissions = async () => {
    try {
      const perms = permissions[selectedRole] || [];

      const res = await fetch(
        `${API}/users/permissions/${selectedRole}`,
        {
          method: "PUT",
          headers: headers(),
          body: JSON.stringify({
            permissions: perms
          }),
        }
      );

      if (!res.ok) {
        const detail = await res.text();

        throw new Error(
          `permission_save_failed_${res.status}: ${detail}`
        );
      }

      const data = await res.json();

      setPermissions(prev => ({
        ...prev,
        [selectedRole]:
          Array.isArray(data.permissions)
            ? data.permissions
            : perms,
      }));

      setPermSaved(true);

      setTimeout(
        () => setPermSaved(false),
        3000
      );

    } catch (error) {
      console.error(
        "Permission save failed:",
        error
      );

      window.alert(
        "Erişim ayarları kaydedilemedi."
      );
    }
  };

  const rolePerms  = permissions[selectedRole] || [];
  const isAllAccess = rolePerms.includes("*");

  const SALES_REGION_LABELS = {
    domestic: { label: `🏠 ${um().regions?.domestic || "Domestic"}`, color: "#10b981" },
    foreign:  { label: `✈️ ${um().regions?.foreign || "Foreign"}`, color: "#8b5cf6" },
    all:      { label: `🌐 ${um().regions?.all || "All"}`, color: "#60a5fa" },
  };

  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary,
      fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@keyframes sf-spin { to{transform:rotate(360deg)} }`}</style>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "28px 24px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0, letterSpacing: "-0.02em" }}>
              🔐 {um().title}
            </h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>
              {um().subtitle}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={fetchData} style={{ padding: "10px 16px", borderRadius: 10,
              border: `1px solid ${t.border.input}`, background: t.bg.toggle,
              color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>🔄</button>
            {mainTab === "users" && (
              <button onClick={() => { setEditing(null); setShowForm(true); }} style={{
                padding: "10px 20px", borderRadius: 10, border: "none",
                background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, boxShadow: t.shadow.btn,
              }}>+ {um().addUser}</button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { label: um().totalUsers, value: users.length, color: t.accent.blue, icon: "👤" },
            { label: um().active,            value: users.filter(u => u.is_active).length,  color: t.accent.green,  icon: "✅" },
            { label: um().passive,            value: users.filter(u => !u.is_active).length, color: t.accent.red,    icon: "⛔" },
            { label: um().roleCount,       value: Object.keys(ROLES).length,              color: t.accent.yellow, icon: "🎭" },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, minWidth: 130, background: t.bg.card,
              border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 12, boxShadow: t.shadow.card }}>
              <span style={{ fontSize: 22 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 600 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Sekmeler */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20,
          background: t.bg.toggle, border: `1px solid ${t.border.input}`,
          borderRadius: 12, padding: 4, width: "fit-content" }}>
          {[
            ["users", `👤 ${um().usersTab}`],
            ["permissions", `🔒 ${um().permissionsTab}`]
          ].map(([v, label]) => (
            <button key={v} onClick={() => { setMainTab(v); localStorage.setItem("tab_usermgmt", v); }} style={{
              padding: "9px 20px", borderRadius: 9, border: "none", cursor: "pointer",
              background: mainTab === v ? (themeName === "dark" ? "rgba(255,255,255,0.12)" : "#fff") : "none",
              color: mainTab === v ? t.text.primary : t.text.muted,
              fontSize: 13, fontWeight: 600,
              boxShadow: mainTab === v ? t.shadow.card : "none", transition: "all 0.2s",
            }}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
            <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`,
              borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
            <span style={{ color: t.text.muted }}>{um().loading}</span>
          </div>
        ) : (
          <>
            {/* ── KULLANICILAR ── */}
            {mainTab === "users" && (
              <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: t.bg.theadRow }}>
                      {[
                          um().username,
                          um().role,
                          um().salesRegion,
                          um().status,
                          um().actions
                        ].map(h => (
                        <th key={h} style={{ padding: "13px 16px", textAlign: "left", fontSize: 11,
                          fontWeight: 700, color: t.text.label, textTransform: "uppercase",
                          letterSpacing: "0.06em" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr><td colSpan={5} style={{ textAlign: "center",
                        color: t.text.placeholder, padding: 40 }}>{um().noUsers}</td></tr>
                    ) : users.map((u, i) => {
                      const roleCfg   = ROLES[u.role] || { label: u.role, color: t.text.muted, icon: "👤" };
                      const isMe      = u.username === currentUser;
                      const isSales   = SALES_ROLES.includes(u.role);
                      const regionCfg = SALES_REGION_LABELS[u.sales_region] || null;
                      return (
                        <tr key={u.id} style={{ borderTop: `1px solid ${t.border.subtle}`,
                          background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ width: 36, height: 36, borderRadius: "50%",
                                background: `${roleCfg.color}22`,
                                border: `1.5px solid ${roleCfg.color}55`,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 16 }}>{roleCfg.icon}</div>
                              <div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>
                                  {u.username}
                                  {isMe && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700,
                                    color: t.accent.blue, background: t.accent.blueBg,
                                    padding: "1px 6px", borderRadius: 20 }}>{um().you}</span>}
                                </div>
                                <div style={{ fontSize: 11, color: t.text.muted }}>ID: {u.id}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "14px 16px" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6,
                              padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                              color: roleCfg.color, background: `${roleCfg.color}18`,
                              border: `1px solid ${roleCfg.color}44` }}>
                              {roleCfg.icon} {roleLabel(u.role)}
                            </span>
                          </td>
                          <td style={{ padding: "14px 16px" }}>
                            {isSales ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                                  color: u.sales_region === "domestic" ? "#10b981" : "#8b5cf6",
                                  background: u.sales_region === "domestic" ? "#10b98118" : "#8b5cf618",
                                  border: `1px solid ${u.sales_region === "domestic" ? "#10b98133" : "#8b5cf633"}`,
                                  width: "fit-content" }}>
                                  {regionCfg?.label || "—"}
                                </span>
                                {u.sales_level && (
                                  <span style={{ fontSize: 10, color: t.text.muted }}>
                                    {u.sales_level === "manager"
                                      ? `🏆 ${um().levels?.manager}`
                                      : u.sales_level === "chief"
                                      ? `⭐ ${um().levels?.chief}`
                                      : `👤 ${um().levels?.representative}`}
                                    {u.reports_to ? ` → ${u.reports_to}` : ""}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span style={{ fontSize: 11, color: t.text.muted }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: "14px 16px" }}>
                            <button onClick={() => !isMe && toggleActive(u)} style={{
                              display: "inline-flex", alignItems: "center", gap: 6,
                              padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                              cursor: isMe ? "default" : "pointer",
                              color: u.is_active ? t.accent.green : t.accent.red,
                              background: u.is_active ? t.accent.greenBg : t.accent.redBg,
                              border: `1px solid ${u.is_active ? t.accent.green : t.accent.red}44`,
                              opacity: isMe ? 0.6 : 1,
                            }}>
                              {u.is_active ? `✅ ${um().active}` : `⛔ ${um().passive}`}
                            </button>
                          </td>
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button onClick={() => { setEditing(u); setShowForm(true); }} style={{
                                padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12,
                                border: `1px solid ${t.accent.blueBorder}`, background: t.accent.blueBg,
                                color: t.accent.blue, fontWeight: 600,
                              }}>✏️ {um().edit}</button>
                              {!isMe && u.username !== "admin" && (
                                <button onClick={() => setDeleteConfirm(u)} style={{
                                  padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12,
                                  border: `1px solid ${t.accent.red}44`, background: t.accent.redBg,
                                  color: t.accent.red, fontWeight: 600,
                                }}>🗑️ {um().delete}</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── MODÜL ERİŞİMLERİ ── */}
            {mainTab === "permissions" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ padding: "12px 16px", background: t.accent.yellowBg,
                  border: `1px solid ${t.accent.yellow}44`, borderRadius: 10,
                  fontSize: 13, color: t.accent.yellow }}>
                  ⚠️ {um().permissionsLocked}
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {Object.entries(ROLES).map(([k, v]) => (
                    <button key={k} onClick={() => setSelectedRole(k)} style={{
                      padding: "8px 16px", borderRadius: 10, cursor: "pointer",
                      border: `2px solid ${selectedRole === k ? v.color : t.border.input}`,
                      background: selectedRole === k ? `${v.color}18` : t.bg.input,
                      color: selectedRole === k ? v.color : t.text.muted,
                      fontSize: 12, fontWeight: 700, transition: "all 0.15s",
                    }}>
                      {v.icon} {roleLabel(k)}
                    </button>
                  ))}
                </div>

                {selectedRole && (
                  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                    borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 20 }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: t.text.primary }}>
                          {ROLES[selectedRole]?.icon} {roleLabel(selectedRole)} — {um().moduleAccess}
                        </div>
                        <div style={{ fontSize: 12, color: t.text.muted, marginTop: 4 }}>
                          {isAllAccess
                            ? um().allModulesAccess
                            : um().modulesAccessCount?.replace(
                                "{count}",
                                String(rolePerms.length)
                              )}
                        </div>
                      </div>
                      {!isAllAccess && (
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => selectAllModulePermissions(selectedRole)}
                            style={{ padding: "7px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12,
                              border: `1px solid ${t.accent.blueBorder}`, background: t.accent.blueBg,
                              color: t.accent.blue, fontWeight: 600 }}>{um().selectAll}</button>
                          <button onClick={() => clearModulePermissions(selectedRole)}
                            style={{ padding: "7px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12,
                              border: `1px solid ${t.border.input}`, background: t.bg.toggle,
                              color: t.text.muted, fontWeight: 600 }}>{um().clear}</button>
                        </div>
                      )}
                    </div>

                    {isAllAccess ? (
                      <div style={{ padding: "20px", textAlign: "center",
                        background: t.accent.greenBg, border: `1px solid ${t.accent.green}44`,
                        borderRadius: 12, color: t.accent.green, fontWeight: 700 }}>
                        ✅ {um().fullAccess}
                      </div>
                    ) : (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 10 }}>
                        {MODULES.map(mod => {
                          const hasAccess = rolePerms.includes(mod.id) || rolePerms.includes("*");
                          return (
                            <div key={mod.id} onClick={() => toggleModulePermission(selectedRole, mod.id)}
                              style={{ display: "flex", alignItems: "center", gap: 10,
                                padding: "12px 16px", borderRadius: 12, cursor: "pointer",
                                border: `2px solid ${hasAccess ? t.accent.green : t.border.input}`,
                                background: hasAccess ? t.accent.greenBg : t.bg.input,
                                transition: "all 0.15s",
                              }}>
                              <div style={{ width: 20, height: 20, borderRadius: 6,
                                border: `2px solid ${hasAccess ? t.accent.green : t.border.input}`,
                                background: hasAccess ? t.accent.green : "none",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0, fontSize: 12 }}>
                                {hasAccess && "✓"}
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600,
                                color: hasAccess ? t.accent.green : t.text.muted }}>
                                {mod.icon} {moduleLabel(mod)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, alignItems: "center" }}>
                  {permSaved && (
                    <span style={{ fontSize: 13, color: t.accent.green, fontWeight: 600 }}>
                      ✅ {um().permissionsSaved}
                    </span>
                  )}
                  <button onClick={savePermissions} style={{
                    padding: "12px 28px", borderRadius: 10, border: "none",
                    background: "linear-gradient(135deg,#059669,#10b981)",
                    color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600,
                    boxShadow: "0 4px 14px rgba(16,185,129,0.3)",
                  }}>💾 {um().savePermissions}</button>
                </div>

                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`,
                  borderRadius: 14, overflow: "auto", boxShadow: t.shadow.card }}>
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${t.border.default}`,
                    fontSize: 14, fontWeight: 700, color: t.text.primary }}>
                    📊 {um().accessSummary}
                  </div>
                  <table style={{ borderCollapse: "collapse", minWidth: "100%" }}>
                    <thead>
                      <tr style={{ background: t.bg.theadRow }}>
                        <th style={{ padding: "10px 16px", textAlign: "left", fontSize: 11,
                          fontWeight: 700, color: t.text.label, textTransform: "uppercase",
                          minWidth: 160 }}>{um().module}</th>
                        {Object.entries(ROLES).map(([k, v]) => (
                          <th key={k} style={{ padding: "10px 8px", textAlign: "center", fontSize: 10,
                            fontWeight: 700, color: v.color, whiteSpace: "nowrap" }}>
                            {v.icon}<br/>{roleLabel(k).split(" ")[0]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {MODULES.map((mod, mi) => (
                        <tr key={mod.id} style={{ borderTop: `1px solid ${t.border.subtle}`,
                          background: mi % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                          <td style={{ padding: "9px 16px", fontSize: 13, fontWeight: 600,
                            color: t.text.primary }}>
                            {mod.icon} {moduleLabel(mod)}
                          </td>
                          {Object.keys(ROLES).map(role => {
                            const perms = permissions[role] || [];
                            const has   = perms.includes("*") || perms.includes(mod.id);
                            return (
                              <td key={role} style={{ padding: "9px 8px", textAlign: "center" }}>
                                <span style={{ fontSize: 14 }}>{has ? "✅" : "—"}</span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {deleteConfirm && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay,
          backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`,
            borderRadius: 16, padding: 28, maxWidth: 380, width: "90%", boxShadow: t.shadow.modal }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.text.primary, marginBottom: 12 }}>
              🗑️ {um().deleteUser}
            </div>
            <div style={{ fontSize: 13, color: t.text.secondary, marginBottom: 20 }}>
              {um().deleteConfirm
                ?.replace("{username}", deleteConfirm.username)}
              <br />
              {um().irreversible}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setDeleteConfirm(null)} style={{
                padding: "9px 18px", borderRadius: 8, border: `1px solid ${t.border.input}`,
                background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13,
              }}>{um().cancel}</button>
              <button onClick={() => handleDelete(deleteConfirm.id)} style={{
                padding: "9px 18px", borderRadius: 8, border: "none",
                background: t.accent.red, color: "#fff", cursor: "pointer",
                fontSize: 13, fontWeight: 600,
              }}>{um().yesDelete}</button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <UserFormModal data={editing} t={t} allUsers={users}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchData(); }} />
      )}
    </div>
  );
}
